import { Control, FieldPath, FieldValues, useController } from 'react-hook-form';
import { Label } from '@givebutter/primitives/label';
import * as RadioGroup from '@givebutter/primitives/radio-group';
import { FormCombobox } from 'components/form/FormCombobox';
import { FormField } from 'components/form/FormField';
import { WixCampaign } from 'lib/gb-api';

const DEFAULT_CAMPAIGN_DESCRIPTION = 'A simple campaign for ongoing website giving.';
const CAMPAIGN_COMBOBOX_THRESHOLD = 10;

interface CampaignPickerProps<T extends FieldValues> {
  name: FieldPath<T>;
  control: Control<T>;
  label?: string;
  campaigns: WixCampaign[];
}

const CampaignPicker = <T extends FieldValues>({
  name,
  control,
  label,
  campaigns,
}: CampaignPickerProps<T>) => {
  if (campaigns.length > CAMPAIGN_COMBOBOX_THRESHOLD) {
    const ordered = [
      ...campaigns.filter(c => c.type === 'general'),
      ...campaigns.filter(c => c.type !== 'general'),
    ];

    return (
      <div className="px-4">
        <FormCombobox
          name={name}
          control={control}
          label={<span className="sr-only">{label}</span>}
          placeholder="Select a campaign…"
          searchPlaceholder="Search campaigns"
          resource="campaign"
          options={ordered.map(c => ({
            value: String(c.id),
            label: c.title,
            searchTerms: [c.code, String(c.id)],
          }))}
        />
      </div>
    );
  }

  return <CampaignRadioPicker name={name} control={control} label={label} campaigns={campaigns} />;
};

const CampaignRadioPicker = <T extends FieldValues>({
  name,
  control,
  label,
  campaigns,
}: CampaignPickerProps<T>) => {
  const {
    field: { value, onChange },
    fieldState: { error },
  } = useController({ name, control });

  const defaultCampaigns = campaigns.filter(c => c.type === 'general');
  const otherCampaigns = campaigns.filter(c => c.type !== 'general');

  return (
    <FormField label={<span className="sr-only">{label}</span>} error={error?.message}>
      <RadioGroup.Root
        value={value as string}
        onValueChange={onChange}
        variant="none"
        className="flex flex-col gap-y-0 border border-gray-100"
      >
        {defaultCampaigns.length > 0 && (
          <>
            <div className="border-b border-gray-100 bg-gray-50 px-3 py-1.5 text-xs text-gray-600">
              Default
            </div>
            {defaultCampaigns.map(campaign => (
              <CampaignPickerItem
                key={campaign.id}
                campaign={campaign}
                description={DEFAULT_CAMPAIGN_DESCRIPTION}
              />
            ))}
          </>
        )}

        {otherCampaigns.length > 0 && (
          <>
            <div className="border-b border-gray-100 bg-gray-50 px-3 py-1.5 text-xs text-gray-600">
              Your active campaigns
            </div>
            {otherCampaigns.map(campaign => (
              <CampaignPickerItem key={campaign.id} campaign={campaign} />
            ))}
          </>
        )}
      </RadioGroup.Root>
    </FormField>
  );
};

interface CampaignPickerItemProps {
  campaign: WixCampaign;
  description?: string;
}

const CampaignPickerItem = ({ campaign, description }: CampaignPickerItemProps) => (
  <RadioGroup.ItemContainer className="items-center gap-3 border-b border-gray-100 px-3 py-3 last:border-b-0">
    <RadioGroup.Item id={String(campaign.id)} value={String(campaign.id)}>
      <RadioGroup.Indicator />
    </RadioGroup.Item>
    <Label htmlFor={String(campaign.id)} className="flex flex-1 cursor-pointer flex-col">
      <span className="text-sm font-medium text-gray-900">{campaign.title}</span>
      {description && <span className="text-xs text-gray-500">{description}</span>}
    </Label>
  </RadioGroup.ItemContainer>
);

export { CampaignPicker };
